import Tween from '../class/Tween';

/**
 * @mixin Gainable
 */
const Gainable = superClass => class extends superClass
{
    #gainNode = null;
    #muted = false;
    #cachedVolume = 1;
    #fadeTween = null;

    /**
     * @memberof Gainable#
     * @type {boolean}
     * @default false
     */
    set muted(muted)
    {
        muted = !!muted;
        const {gainNode} = this;

        if (gainNode)
        {
            if (muted)
            {
                this.#cachedVolume = gainNode.gain.value;
                gainNode.gain.value = 0;
            }
            else
            {
                gainNode.gain.value = this.#cachedVolume;
            }
        }

        this.#muted = muted;
    }

    get muted()
    {
        return this.#muted;
    }

    /**
     * @memberof Gainable#
     * @type {Number}
     * @default 1
     */
    set volume(volume)
    {
        const {gainNode} = this;

        if (!this.#muted && gainNode)
        {
            this.#gainNode.gain.value = volume;
        }

        this.#cachedVolume = volume;
    }

    get volume()
    {
        return this.#muted ? this.#cachedVolume : this.#gainNode.gain.value;
    }

    /** Getter
     * @memberof Gainable#
     * @type {GainNode}
     */
    get gainNode()
    {
        return this.#gainNode || this.#createGainNode();
    }

    /** Getter
     * @memberof Gainable#
     * @type {Tween}
     */
    get fadeTween()
    {
        return this.#fadeTween;
    }

    /**
     * @memberof Gainable#
     * @param {Number} to Zielvolume.
     * @param {Number} duration Dauern des Fades
     * @returns {this}
     */
    fade(to, duration)
    {
        if (!this.#gainNode) { return this; }

        this._destroyFadeTween();

        this.#fadeTween = new Tween(this, duration, {volume: to}, Tween.ease.outSine);
        this.#fadeTween.once('complete', () => this._destroyFadeTween());

        return this;
    }

    #createGainNode()
    {
        const {context} = Gainable;
        this.#gainNode = (typeof context.createGain === 'undefined') ?
            context.createGainNode() : context.createGain();

        if (this.#muted)
        {
            this.muted = true;
        }
        else if (this.#cachedVolume !== 1)
        {
            this.volume = this.#cachedVolume;
        }

        return this.#gainNode;
    }

    _destroyFadeTween()
    {
        if (this.#fadeTween)
        {
            this.#fadeTween.destroy();
            this.#fadeTween = null;
        }
    }

    _destroyGainNode()
    {
        if (this.#gainNode)
        {
            this.#gainNode.disconnect();
            this.#gainNode = null;
        }
    }
};

Gainable.sorting = 0;

export default Gainable;
